import {Injectable} from '@angular/core';
import {environment} from '../../../environments/environment';

const KEY = 'access_token';
const CODE = 'code';
const APARTMENT = 'apartment';
const DETECT_MOBILE = 'detect_mobile';
const CHECKING_WARNING = 'checking_warning';
const CONTRACT_READ = 'contract_read';
const INSTALL_WARNING = 'install_warning';
const SHOW_CHECK_IN_EMAIL_FIELD = 'show_check_in_email_field';
const WEGLOT_LANG = 'weglot_lang';
const TRAVEL_REGISTER_ID = 'travel_register_id';
const CONTRACT_URL = 'contract_url';
const EMAIL = 'email';
const SIGNED_CONTRACT_URL = 'signed_contract_url';
const SIGNED_TRAVEL_REGISTER_FILE_URL = 'signed_travel_register_file_url';
const BRANDING_CODE = 'branding_code';
const CHECK_IN_TYPE = 'check_in_type';

@Injectable({providedIn: 'root'})
export class HoomvipStorage {
  getToken(): string | null {
    return localStorage.getItem(KEY);
  }

  getCode(): string {
    return localStorage.getItem(CODE) || '';
  }

  setSession(token: string, code: string): void {
    localStorage.setItem(KEY, token);
    localStorage.setItem(CODE, code);
  }

  unmarkCheckingWarning(): void {
    sessionStorage.removeItem(CHECKING_WARNING);
  }

  markCheckingWarning(): void {
    sessionStorage.setItem(CHECKING_WARNING, '1');
  }

  existsCheckingWarning(): boolean {
    return sessionStorage.getItem(CHECKING_WARNING) === '1';
  }

  setApartment(apartment: number): void {
    localStorage.setItem(APARTMENT, apartment.toString());
  }

  getApartment(): number | null {
    return Number(localStorage.getItem(APARTMENT) ?? null);
  }

  public currentLang(): string {
    return localStorage.getItem(WEGLOT_LANG) || environment.defaultAppLang;
  }

  public setCurrentLang(lang: string): void {
    localStorage.setItem(WEGLOT_LANG, lang);
  }

  public setContractRead(): void {
    sessionStorage.setItem(CONTRACT_READ, '1');
  }

  public isContractRead(): boolean {
    return sessionStorage.getItem(CONTRACT_READ) === '1';
  }

  clear(): void {
    localStorage.removeItem(KEY);
    localStorage.removeItem(CODE);
    localStorage.removeItem(APARTMENT);
    sessionStorage.removeItem(DETECT_MOBILE);
    sessionStorage.removeItem(CHECKING_WARNING);
    sessionStorage.removeItem(CONTRACT_READ);
    localStorage.removeItem(WEGLOT_LANG);
    this.clearCheckInResponseInfo();
    this.clearSignatureInfo();
  }

  shouldDetectMobile() {
    if (sessionStorage.getItem(DETECT_MOBILE) === null) {
      return true;
    }
    return sessionStorage.getItem(DETECT_MOBILE) === 'true';
  }

  setDetectMobile(value: boolean) {
    sessionStorage.setItem(DETECT_MOBILE, value.toString());
  }

  setCheckInSignatureInfo(travelRegisterId: string, contractUrl: string, email: string) {
    localStorage.setItem(TRAVEL_REGISTER_ID, travelRegisterId);
    localStorage.setItem(CONTRACT_URL, contractUrl);
    localStorage.setItem(EMAIL, email);
  }

  clearCheckInInfo() {
    this.clearCheckInResponseInfo();
    this.clearSignatureInfo();
  }

  setCheckInMinorSignatureInfo(travelRegisterId: string, email: string) {
    localStorage.setItem(TRAVEL_REGISTER_ID, travelRegisterId);
    localStorage.setItem(EMAIL, email);
  }

  clearCheckInResponseInfo() {
    localStorage.removeItem(TRAVEL_REGISTER_ID);
    localStorage.removeItem(CONTRACT_URL);
    localStorage.removeItem(EMAIL);
    localStorage.removeItem(SHOW_CHECK_IN_EMAIL_FIELD);
  }

  clearSignatureInfo() {
    localStorage.removeItem(SIGNED_CONTRACT_URL);
    localStorage.removeItem(SIGNED_TRAVEL_REGISTER_FILE_URL);
  }

  getCheckInEmail(): string | null {
    let email = localStorage.getItem(EMAIL);
    if (email === null || email === 'null') {
      return null;
    }
    return email;
  }

  getCheckInTravelRegisterId(): string | null {
    return localStorage.getItem(TRAVEL_REGISTER_ID);
  }

  getCheckInContractUrl(): string | null {
    return localStorage.getItem(CONTRACT_URL);
  }

  setSignatureInfo(signedContractUrl: string, signedTravelRegisterFileURL: string) {
    localStorage.setItem(SIGNED_CONTRACT_URL, signedContractUrl);
    localStorage.setItem(SIGNED_TRAVEL_REGISTER_FILE_URL, signedTravelRegisterFileURL);
  }

  getSignedContractUrl(): string {
    return localStorage.getItem(SIGNED_CONTRACT_URL) || '';
  }

  getSignedTravelRegisterFileUrl(): string {
    return localStorage.getItem(SIGNED_TRAVEL_REGISTER_FILE_URL) || '';
  }

  setCheckInEmail(email: string) {
    localStorage.setItem(EMAIL, email);
    localStorage.setItem(SHOW_CHECK_IN_EMAIL_FIELD, 'true');
  }

  getShowCheckInEmailDialog(): boolean {
    return localStorage.getItem(SHOW_CHECK_IN_EMAIL_FIELD) === 'true';
  }

  getInstallWarning(): boolean {
    return localStorage.getItem(INSTALL_WARNING) === '1';
  }

  markInstallWarning() {
    localStorage.setItem(INSTALL_WARNING, '1');
  }

  storeCurrentBrandingCode(currentBrandingCodeFromUrl: string | null) {
    if (currentBrandingCodeFromUrl) {
      sessionStorage.setItem(BRANDING_CODE, currentBrandingCodeFromUrl);
    }
  }

  getCurrentBrandingCode(): string | null {
    return sessionStorage.getItem(BRANDING_CODE);
  }

  getBrandingCode(): string | null {
    const brandingCodeFromUrl = this.getBrandingCodeFromUrl();

    if (brandingCodeFromUrl) {
      this.storeCurrentBrandingCode(brandingCodeFromUrl);
      return brandingCodeFromUrl;
    }

    return this.getCurrentBrandingCode()?.trim() || null;
  }

  private getBrandingCodeFromUrl(): string | null {
    const params = new URLSearchParams(window.location.search);
    return params.get(environment.brandingCodeQueryParam)?.trim() || null;
  }

  setCheckInType(checkinType: string) {
    localStorage.setItem(CHECK_IN_TYPE, checkinType);
  }

  getCheckInType(): string | null {
    return localStorage.getItem(CHECK_IN_TYPE);
  }
}
